import { get } from 'lodash-es';
import { useAppSelector } from 'hooks/reduxHooks';
import language from 'config/language';
import useMismatchError from './useMismatchError';

type UseWrongInviteEmailError = {
  hasWrongInviteEmailError: boolean;
  dismissWrongInviteEmailError: () => void;
  wrongInviteEmailErrorMessage: string;
};

/**
 * Detects whether a successfully logged in microsoft 365 user
 * has an invalid invite token (i.e. does not match the email).
 *
 * @returns
 * @property hasWrongInviteEmailError: true if this error has obtained.
 * @property dismissWrongInviteEmailError: function to change hasWrongInviteEmailError state to false
 * @property wrongInviteEmailErrorMessage: error message to display to the user.
 */
const useWrongInviteEmailError = (): UseWrongInviteEmailError => {
  /** user from current stored token */
  const user = useAppSelector((state) => get(state, 'auth.user', {}));

  /**
   * inviteEmail - the email by which the user was invited.
   * validated - whether this email has been validated as
   * belonging to the invite token.
   * */
  const { email: inviteEmail, validated: inviteValidated } = useAppSelector(
    (state) => get(state, 'auth.invite'),
  );

  const { mismatchError, setMismatchFalse } = useMismatchError(
    !!(user && user.email !== inviteEmail && inviteValidated),
    true, // logout is required in this case - otherwise the user can't sign in with a different account.
    false, // 302 redirect is disabled otherwise the user is redirected back to login and error state is lost.
  );

  const wrongInviteEmailErrorMessage: string =
    language.auth.microsoft_signup.errors.invite_email_mismatch;

  return {
    hasWrongInviteEmailError: mismatchError,
    dismissWrongInviteEmailError: setMismatchFalse,
    wrongInviteEmailErrorMessage,
  };
};

export default useWrongInviteEmailError;
